import { z } from "zod";
import { ONLINE_PAYMENT_PROVIDERS, ONLINE_PAYMENT_STATUSES, SETTLEMENT_STATUSES } from "../enums";
import type { OnlinePaymentProvider, OnlinePaymentStatus, SettlementStatus } from "../enums";

// Encaissements: cash on delivery collected by carriers and paid out to the merchant (settlements), online payment
// links (Konnect, Flouci) and the merchant's gateway credentials. Amounts are integer minor units.

const id = z.string().regex(/^[a-f0-9]{24}$/i, "Identifiant invalide");
const minor = z.number().int("Montant invalide").min(0, "Montant invalide");
const isoDate = z.string().datetime({ offset: true });

export const codShipmentsQuery = z.object({
  carrierCode: z.string().trim().min(1).max(60).optional(),
  /** collected = delivered and not settled; pending = still on the road. */
  state: z.enum(["collected", "pending"]).default("collected"),
  /** Only shipments not attached to any settlement yet. */
  unassigned: z.enum(["true", "false"]).transform((v) => v === "true").optional(),
  from: isoDate.optional(),
  to: isoDate.optional(),
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(500).default(100),
});
export type CodShipmentsQuery = z.infer<typeof codShipmentsQuery>;

export const settlementListQuery = z.object({
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(200).default(25),
  status: z.enum([...SETTLEMENT_STATUSES, "all"] as const).default("all"),
  carrierCode: z.string().trim().max(60).optional(),
});
export type SettlementListQuery = z.infer<typeof settlementListQuery>;

export const settlementCreateSchema = z.object({
  carrierCode: z.string().trim().min(1, "Transporteur requis").max(60),
  shipmentIds: z.array(id).min(1, "Choisissez au moins une livraison").max(2000),
  reference: z.string().trim().max(120).optional().default(""),
  periodFrom: isoDate.nullable().optional(),
  periodTo: isoDate.nullable().optional(),
  receivedMinor: minor.optional().default(0),
  feesMinor: minor.optional().default(0),
  status: z.enum(SETTLEMENT_STATUSES).optional().default("pending"),
  notes: z.string().trim().max(2000).optional().default(""),
});
export type SettlementCreateInput = z.infer<typeof settlementCreateSchema>;

export const settlementUpdateSchema = z.object({
  shipmentIds: z.array(id).min(1).max(2000).optional(),
  reference: z.string().trim().max(120).optional(),
  periodFrom: isoDate.nullable().optional(),
  periodTo: isoDate.nullable().optional(),
  receivedMinor: minor.optional(),
  feesMinor: minor.optional(),
  status: z.enum(SETTLEMENT_STATUSES).optional(),
  notes: z.string().trim().max(2000).optional(),
});
export type SettlementUpdateInput = z.infer<typeof settlementUpdateSchema>;

export const onlinePaymentListQuery = z.object({
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(200).default(25),
  status: z.enum([...ONLINE_PAYMENT_STATUSES, "all"] as const).default("all"),
  provider: z.enum(ONLINE_PAYMENT_PROVIDERS).optional(),
  orderId: id.optional(),
});
export type OnlinePaymentListQuery = z.infer<typeof onlinePaymentListQuery>;

export const orderPaymentLinkSchema = z.object({
  orderId: id,
  provider: z.enum(ONLINE_PAYMENT_PROVIDERS),
});
export type OrderPaymentLinkInput = z.infer<typeof orderPaymentLinkSchema>;

/** Konnect: API key + wallet id. Empty secret = keep the stored value. */
export const konnectConnectionSchema = z.object({
  apiKey: z.string().trim().max(400).optional().default(""),
  walletId: z.string().trim().max(120).optional().default(""),
  sandbox: z.boolean().optional().default(false),
});

/** Flouci: public app token + private app secret (also the webhook signing key). Empty = keep the stored value. */
export const flouciConnectionSchema = z.object({
  appToken: z.string().trim().max(400).optional().default(""),
  appSecret: z.string().trim().max(400).optional().default(""),
  sandbox: z.boolean().optional().default(false),
});

export interface CodBucketDto {
  count: number;
  amountMinor: number;
}

export interface CodCarrierRowDto {
  carrierCode: string;
  pending: CodBucketDto;
  collected: CodBucketDto;
  settled: CodBucketDto;
  /** Collected and already attached to a settlement waiting for the payout. */
  inSettlement: CodBucketDto;
}

export interface CodOverviewDto {
  currency: string;
  totals: {
    /** Still on the road (shipment not delivered yet). */
    pending: CodBucketDto;
    /** Delivered: the carrier holds the cash. */
    collected: CodBucketDto;
    /** Paid out to the merchant. */
    settled: CodBucketDto;
    /** Sum of (expected - received - fees) on settlements that are not fully reconciled. */
    differenceMinor: number;
    feesMinor: number;
    openSettlements: number;
  };
  carriers: CodCarrierRowDto[];
}

export interface CodShipmentDto {
  id: string;
  orderId: string;
  orderNumber: string;
  customerName: string;
  carrierCode: string;
  trackingNumber: string | null;
  status: string;
  codAmountMinor: number;
  codState: string;
  deliveredAt: string | null;
  settlementId: string | null;
  createdAt: string;
}

export interface CodSettlementDto {
  id: string;
  carrierCode: string;
  reference: string;
  periodFrom: string | null;
  periodTo: string | null;
  shipmentIds: string[];
  shipmentsCount: number;
  expectedMinor: number;
  receivedMinor: number;
  feesMinor: number;
  /** expected - received - fees: positive = money missing. */
  differenceMinor: number;
  currency: string;
  status: SettlementStatus;
  settledAt: string | null;
  notes: string;
  createdAt: string;
  updatedAt: string;
}

export interface CodSettlementDetailDto extends CodSettlementDto {
  shipments: CodShipmentDto[];
}

export interface OnlinePaymentDto {
  id: string;
  orderId: string;
  orderNumber: string | null;
  customerName: string | null;
  provider: OnlinePaymentProvider;
  amountMinor: number;
  currency: string;
  status: OnlinePaymentStatus;
  paymentUrl: string | null;
  expiresAt: string | null;
  paidAt: string | null;
  error: string | null;
  createdAt: string;
}

export interface PaymentGatewayConnectionDto {
  provider: OnlinePaymentProvider;
  configured: boolean;
  status: "pending" | "active" | "failed" | "disabled" | "none";
  sandbox: boolean;
  verifiedAt: string | null;
  lastError: string | null;
  /** Masked credential values (never the secrets themselves). */
  masked: Record<string, string | null>;
  updatedAt: string | null;
}
